/**
 * webVitals.js — report Core Web Vitals to GA4.
 *
 * Fires LCP / CLS / INP / FCP / TTFB as GA4 events so real-user
 * performance is visible in the same dashboard where activation and
 * engagement live. The metrics are reported with GA4's own segmentation
 * dimensions (country, device_category) so we can slice by "budget
 * Android in Bangladesh" rather than lab-only Lighthouse runs.
 *
 * Cost: web-vitals v4 is < 2 KB gzipped. Loaded once after first
 * paint via requestIdleCallback so it never blocks activation.
 */

// Map a raw metric value into GA4-friendly integer units per Google's
// recommended conventions:
//   • CLS   → x1000 (unitless score becomes an integer)
//   • other → rounded ms
function toIntValue(name, value) {
  if (name === "CLS") return Math.round((value || 0) * 1000);
  return Math.round(value || 0);
}

// GA4 event names use snake_case; we keep them short and consistent.
const NAME_MAP = {
  LCP: "web_vitals_lcp",
  CLS: "web_vitals_cls",
  INP: "web_vitals_inp",
  FCP: "web_vitals_fcp",
  TTFB: "web_vitals_ttfb",
};

// A basic device_category hint so we can segment by mid/low-end
// Android without waiting on GA4's own device_category (which is
// server-side, so only appears in reports, not live).
function deviceHint() {
  try {
    const ua = navigator.userAgent || "";
    const mem = navigator.deviceMemory || 0;
    const cores = navigator.hardwareConcurrency || 0;
    const isAndroid = /Android/i.test(ua);
    const budget = mem && mem <= 2;
    if (isAndroid && budget) return "android_low";
    if (isAndroid && cores && cores <= 4) return "android_mid";
    if (isAndroid) return "android_high";
    if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
    return "desktop";
  } catch { return "unknown"; }
}

function send(metric) {
  try {
    const eventName = NAME_MAP[metric.name] || `web_vitals_${(metric.name || "").toLowerCase()}`;
    const params = {
      metric_id: metric.id,
      metric_name: metric.name,
      metric_value: toIntValue(metric.name, metric.value),
      metric_rating: metric.rating,      // "good" | "needs-improvement" | "poor"
      metric_delta: Math.round(metric.delta || 0),
      device_hint: deviceHint(),
      value: toIntValue(metric.name, metric.value), // GA4 numeric "value"
    };
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
    // Also expose to the internal analytics ring so /experience-quality
    // can surface these when we build a perf tile.
    if (typeof window !== "undefined") {
      try {
        window.dispatchEvent(new CustomEvent("mwq-web-vital", { detail: params }));
      } catch { /* ignore */ }
    }
  } catch { /* ignore */ }
}

let started = false;

/** Idempotent — safe to call multiple times. */
export function initWebVitals() {
  if (started || typeof window === "undefined") return;
  started = true;
  const boot = () => {
    import("web-vitals").then(({ onLCP, onCLS, onINP, onFCP, onTTFB }) => {
      onLCP(send);
      onCLS(send);
      onINP(send);
      onFCP(send);
      onTTFB(send);
    }).catch(() => { /* fail silent — perf reporting must never break the app */ });
  };
  // Defer until the browser is idle so the reporter never contends
  // with LCP-critical resources.
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(boot, { timeout: 3000 });
  } else {
    setTimeout(boot, 1500);
  }
}
